import { create } from "zustand";

/**
 * What the dock needs to know about a room you are tuned into (§6.8).
 *
 * ## Why this is separate from the player store
 *
 * The player store answers "what is this browser playing". This one answers
 * "is that playback following a room, and where is the room". They are
 * different questions with different lifetimes - you can leave a room page and
 * keep listening, and you can start a normal track and stop following a room -
 * and folding the second into the first would put a room-shaped branch in every
 * method of a store that four other features depend on.
 *
 * ## The playhead lives here, not in the player
 *
 * `PlayerHost` reads it to correct drift. It is written by whatever component
 * is watching the room, which is deliberately not the player's business: the
 * player knows how to play audio, and the room knows where the audio should be.
 */
interface Tuned {
  id: number;
  /** The host. A broadcast's address is their profile, not a room URL. */
  username: string;
  /** What tonight's set is called, if it is called anything. */
  name: string | null;
  /**
   * The host's display name.
   *
   * Carried rather than looked up because the dock shows it from every page on
   * the site, and most of those have never heard of this host - a fetch per
   * navigation to render one line would be absurd.
   */
  host: string;
  /**
   * What the room is playing, when the dock cannot know it any other way.
   *
   * In catalogue mode the dock reads the player, because the player is what is
   * playing. In studio mode nothing is loaded into it - the audio arrives
   * inside the host's stream - so without this the dock would say "between
   * tracks" over a record the listener can plainly hear.
   *
   * Null in catalogue mode, where the player is the better source, and null
   * between tracks in either.
   */
  streaming: { title: string; artist: string } | null;
  /**
   * Whether this broadcast comes from the host's studio rather than the
   * catalogue.
   *
   * The dock hides itself for these. It is a transport for something *it* is
   * playing, and in studio mode it is playing nothing - the sound is inside a
   * video element on the host's profile. A bar offering pause and volume for
   * audio it does not own is a lie, and it was showing "between tracks" over a
   * record people could hear.
   */
  studio: boolean;
}

/** The room's playhead as last reported, and when it was true. */
interface Playhead {
  position_ms: number | null;
  server_time: string;
  paused: boolean;
  /** The track the room is on, so the player can tell a change from a drift. */
  track_id: number | null;
}

interface RoomState {
  tuned: Tuned | null;
  playhead: Playhead | null;

  tune: (room: Tuned, playhead: Playhead) => void;
  /** Update only what a studio broadcast is playing. No-op when untuned. */
  retune: (streaming: Tuned["streaming"]) => void;
  update: (playhead: Playhead) => void;
  /**
   * Stop following.
   *
   * Does not stop the audio: somebody who skips to a different track from the
   * dock has left the room but is still listening to something, and silencing
   * them would be a surprising punishment for pressing next.
   */
  leave: () => void;
}

export const useRoomTuning = create<RoomState>()((set) => ({
  tuned: null,
  playhead: null,

  tune: (room, playhead) => set({ tuned: room, playhead }),

  /*
   * Ignored when nothing is tuned.
   *
   * A room page updates its own state on a timer, and without this guard a
   * stale interval firing after `leave()` would silently re-tune somebody who
   * had walked away from the room.
   */
  update: (playhead) =>
    set((state) => (state.tuned === null ? state : { playhead })),

  retune: (streaming) =>
    set((state) => {
      if (state.tuned === null) return state;

      // Compared by value, because this runs on every poll and a fresh object
      // each time would re-render the dock four times a second.
      const same =
        state.tuned.streaming?.title === streaming?.title &&
        state.tuned.streaming?.artist === streaming?.artist;

      return same ? state : { tuned: { ...state.tuned, streaming } };
    }),

  leave: () => set({ tuned: null, playhead: null }),
}));
